/**
 * Playlist Overlay Controller
 * Manages the expandable playlist overlay in the transport bar
 */

import { playlistState, playlistOverlayState, lyricsState, uiState, sortingState } from './state.js';
import { elements } from './dom-cache.js';
import { formatTime, formatPlaylistDuration, loadTrack, startPlayback, isPlaybackBlocked } from './playback.js';
import { recalculateLyricsScrollParams } from './visualizer-controls.js';
import { cleanupModal } from './cleanup-modal.js';
import { sortingModal } from './sorting-modal.js';
import { escapeHtml } from '../lib/html-utils.js';

/**
 * Initialize playlist overlay
 */
export function initPlaylistOverlay() {
  setupTriggerButton();
  setupHeaderButtons();
  setupLyricsPanelButtons();
  setupKeyboardShortcuts();
  setupCleanUpButton();
  setupSortingToggles();
  setupFilterInput();
  setupSunoFilters();
  setupBulkExcludeButton();
  setupCurrentSongResizeObserver();

  // Initialize the cleanup modal
  cleanupModal.init();

  // Initialize the sorting modal
  sortingModal.init();

  // Listen for playlist changes from other modules
  document.addEventListener('playlistUpdated', () => {
    updateOverlayUI();
    updateCleanUpButtonState();
    updateBulkExcludeButtonState();
    // Preserve custom order when playlist changes
    updateCustomOrderFromPlaylist();
  });

  // Listen for durations loaded event
  document.addEventListener('playlistDurationsLoaded', () => {
    updateCleanUpButtonState();
    updateOverlayDurationDisplay();
  });

  console.log('[PlaylistOverlay] Initialized');
}

/**
 * Setup clean up button
 */
function setupCleanUpButton() {
  if (!elements.cleanUpBtn) return;

  elements.cleanUpBtn.addEventListener('click', () => {
    if (!elements.cleanUpBtn.disabled) {
      cleanupModal.show();
    }
  });

  // Initial state
  updateCleanUpButtonState();
}

/**
 * Update clean up button enabled state based on duration loading
 */
function updateCleanUpButtonState() {
  if (!elements.cleanUpBtn) return;

  const hasPlaylist = playlistState.playlist.length > 0;
  const durationsLoaded = playlistState.durationsLoaded;

  if (!hasPlaylist) {
    elements.cleanUpBtn.disabled = true;
    elements.cleanUpBtn.title = 'No files in playlist';
  } else if (!durationsLoaded) {
    elements.cleanUpBtn.disabled = true;
    elements.cleanUpBtn.title = 'Durations still loading...';
  } else {
    elements.cleanUpBtn.disabled = false;
    elements.cleanUpBtn.title = 'Clean up playlist';
  }
}

/**
 * Setup trigger button in transport bar
 */
function setupTriggerButton() {
  if (!elements.playlistTriggerBtn) return;

  elements.playlistTriggerBtn.addEventListener('click', () => {
    toggleOverlay();
  });
}

/**
 * Setup header action buttons
 */
function setupHeaderButtons() {
  // Collapse button
  if (elements.collapsePlaylistOverlayBtn) {
    elements.collapsePlaylistOverlayBtn.addEventListener('click', () => {
      closeOverlay();
    });
  }

  // Toggle excluded button
  if (elements.toggleExcludedBtn) {
    elements.toggleExcludedBtn.addEventListener('click', () => {
      toggleExcludedPanel();
    });
  }

  // Close excluded button
  if (elements.closeExcludedBtn) {
    elements.closeExcludedBtn.addEventListener('click', () => {
      hideExcludedPanel();
    });
  }
}

/**
 * Setup lyrics panel buttons
 */
function setupLyricsPanelButtons() {
  // Close lyrics panel button
  if (elements.closeLyricsOverlayBtn) {
    elements.closeLyricsOverlayBtn.addEventListener('click', () => {
      hideLyricsPanel();
    });
  }

  // Swap lyrics button
  if (elements.swapLyricsOverlayBtn) {
    elements.swapLyricsOverlayBtn.addEventListener('click', () => {
      if (elements.lyricsOverlayFileInput) {
        elements.lyricsOverlayFileInput.click();
      }
    });
  }

  // Clear lyrics button
  if (elements.clearLyricsOverlayBtn) {
    elements.clearLyricsOverlayBtn.addEventListener('click', () => {
      clearOverlayLyrics();
    });
  }

  // Select file button in dropzone
  if (elements.lyricsOverlaySelectBtn) {
    elements.lyricsOverlaySelectBtn.addEventListener('click', () => {
      if (elements.lyricsOverlayFileInput) {
        elements.lyricsOverlayFileInput.click();
      }
    });
  }

  // File input change handler
  if (elements.lyricsOverlayFileInput) {
    elements.lyricsOverlayFileInput.addEventListener('change', async (e) => {
      const file = e.target.files[0];
      if (file && playlistOverlayState.lyricsTrackIndex >= 0) {
        await loadLyricsFile(file, playlistOverlayState.lyricsTrackIndex);
      }
      e.target.value = ''; // Reset for next selection
    });
  }

  // Setup drag and drop for lyrics dropzone
  const dropzone = elements.lyricsOverlayDropzone;
  if (dropzone) {
    dropzone.addEventListener('dragover', (e) => {
      e.preventDefault();
      dropzone.classList.add('drag-over');
    });

    dropzone.addEventListener('dragleave', () => {
      dropzone.classList.remove('drag-over');
    });

    dropzone.addEventListener('drop', async (e) => {
      e.preventDefault();
      dropzone.classList.remove('drag-over');

      const file = e.dataTransfer.files[0];
      if (file && file.name.endsWith('.txt') && playlistOverlayState.lyricsTrackIndex >= 0) {
        await loadLyricsFile(file, playlistOverlayState.lyricsTrackIndex);
      }
    });
  }
}

/**
 * Load lyrics from a file for a specific track
 */
async function loadLyricsFile(file, trackIndex) {
  try {
    const text = await file.text();
    // Look for "--- Lyrics ---" section or use entire file
    const lyricsMatch = text.match(/---\s*Lyrics\s*---\s*\n([\s\S]*)/i);
    const lyrics = lyricsMatch ? lyricsMatch[1].trim() : text.trim();

    if (lyrics) {
      playlistState.playlist[trackIndex].lyrics = lyrics;
      playlistState.playlist[trackIndex].lyricsFile = file;

      // Update the lyrics panel display
      updateLyricsPanelContent(trackIndex);

      // Recalculate scroll params if this is the current track
      if (trackIndex === playlistState.currentTrackIndex) {
        recalculateLyricsScrollParams();
      }

      // Update overlay UI to refresh lyrics button states
      updateOverlayUI();

      // Dispatch event for sidebar sync
      document.dispatchEvent(new CustomEvent('playlistUpdated'));
    }
  } catch (err) {
    console.error('[PlaylistOverlay] Failed to load lyrics file:', err);
  }
}

/**
 * Clear lyrics for the track shown in overlay lyrics panel
 */
function clearOverlayLyrics() {
  const trackIndex = playlistOverlayState.lyricsTrackIndex;
  if (trackIndex >= 0 && playlistState.playlist[trackIndex]) {
    playlistState.playlist[trackIndex].lyrics = null;
    playlistState.playlist[trackIndex].lyricsFile = null;

    // Update the panel to show dropzone
    updateLyricsPanelContent(trackIndex);

    // Clear scroll params if this is the current track
    if (trackIndex === playlistState.currentTrackIndex) {
      lyricsState.currentLyricsScrollParams = null;
    }

    // Update overlay UI to refresh lyrics button states
    updateOverlayUI();

    // Dispatch event for sidebar sync
    document.dispatchEvent(new CustomEvent('playlistUpdated'));
  }
}

/**
 * Toggle overlay open/closed
 */
export function toggleOverlay() {
  if (playlistOverlayState.isOpen) {
    closeOverlay();
  } else {
    openOverlay();
  }
}

/**
 * Open the playlist overlay
 */
export function openOverlay() {
  if (!elements.playlistOverlay) return;

  playlistOverlayState.isOpen = true;
  elements.playlistOverlay.classList.add('open');
  elements.playlistOverlayContainer?.classList.add('expanded');
  elements.playlistTriggerBtn?.classList.add('active');

  updateOverlayUI();

  // Recreate all icons in the overlay
  if (window.lucide) {
    window.lucide.createIcons();
  }

  document.dispatchEvent(new CustomEvent('playlistOverlayOpened'));
}

/**
 * Close the playlist overlay
 */
export function closeOverlay() {
  if (!elements.playlistOverlay) return;

  playlistOverlayState.isOpen = false;
  elements.playlistOverlay.classList.remove('open');
  elements.playlistOverlayContainer?.classList.remove('expanded');
  elements.playlistTriggerBtn?.classList.remove('active');

  // Also hide excluded, lyrics, and Suno filter panels
  hideExcludedPanel();
  hideLyricsPanel();
  closeSunoFilterPanel();

  // Clear filter when closing
  clearFilter();

  document.dispatchEvent(new CustomEvent('playlistOverlayClosed'));
}

/**
 * Toggle excluded panel visibility
 */
function toggleExcludedPanel() {
  if (playlistOverlayState.excludedVisible) {
    hideExcludedPanel();
  } else {
    showExcludedPanel();
  }
}

/**
 * Show excluded panel
 */
function showExcludedPanel() {
  if (!elements.playlistOverlayExcluded) return;

  // Close lyrics panel if open (mutually exclusive)
  if (playlistOverlayState.lyricsVisible) {
    hideLyricsPanel();
  }

  playlistOverlayState.excludedVisible = true;
  elements.playlistOverlayExcluded.classList.add('visible');

  // Update toggle button icon
  updateExcludedToggleIcon(true);

  updateExcludedList();
}

/**
 * Hide excluded panel
 */
function hideExcludedPanel() {
  if (!elements.playlistOverlayExcluded) return;

  playlistOverlayState.excludedVisible = false;
  elements.playlistOverlayExcluded.classList.remove('visible');

  // Clear selection of excluded items when panel is hidden
  const excludedIndices = playlistState.selectedIndices.filter(idx =>
    idx >= 0 && idx < playlistState.playlist.length && !playlistState.playlist[idx].included
  );
  if (excludedIndices.length > 0) {
    playlistState.selectedIndices = playlistState.selectedIndices.filter(idx =>
      playlistState.playlist[idx]?.included
    );
  }

  // Update toggle button icon
  updateExcludedToggleIcon(false);
}

/**
 * Show lyrics panel for a track
 */
function showLyricsPanel(trackIndex) {
  if (!elements.playlistOverlayLyrics) return;

  // Close excluded panel if open (mutually exclusive)
  if (playlistOverlayState.excludedVisible) {
    hideExcludedPanel();
  }

  playlistOverlayState.lyricsVisible = true;
  playlistOverlayState.lyricsTrackIndex = trackIndex;
  elements.playlistOverlayLyrics.classList.add('visible');

  // Update content
  updateLyricsPanelContent(trackIndex);

  // Recreate icons
  if (window.lucide) {
    window.lucide.createIcons({ nodes: [elements.playlistOverlayLyrics] });
  }
}

/**
 * Hide lyrics panel
 */
function hideLyricsPanel() {
  if (!elements.playlistOverlayLyrics) return;

  playlistOverlayState.lyricsVisible = false;
  playlistOverlayState.lyricsTrackIndex = -1;
  elements.playlistOverlayLyrics.classList.remove('visible');
}

/**
 * Update lyrics panel content for a track
 */
function updateLyricsPanelContent(trackIndex) {
  const track = playlistState.playlist[trackIndex];
  if (!track) return;

  // Update track name
  if (elements.lyricsOverlayTrack) {
    elements.lyricsOverlayTrack.textContent = track.name;
  }

  // Show lyrics text or dropzone
  if (track.lyrics) {
    if (elements.lyricsOverlayText) {
      elements.lyricsOverlayText.textContent = track.lyrics;
      elements.lyricsOverlayText.style.display = 'block';
    }
    if (elements.lyricsOverlayDropzone) {
      elements.lyricsOverlayDropzone.style.display = 'none';
    }
    if (elements.lyricsOverlayActions) {
      elements.lyricsOverlayActions.style.display = 'flex';
    }
  } else {
    if (elements.lyricsOverlayText) {
      elements.lyricsOverlayText.style.display = 'none';
    }
    if (elements.lyricsOverlayDropzone) {
      elements.lyricsOverlayDropzone.style.display = 'flex';
    }
    if (elements.lyricsOverlayActions) {
      elements.lyricsOverlayActions.style.display = 'none';
    }
  }
}

/**
 * Handle lyrics button click in overlay
 */
function handleLyricsClick(trackIndex) {
  if (playlistOverlayState.lyricsVisible && playlistOverlayState.lyricsTrackIndex === trackIndex) {
    // Same track - toggle off
    hideLyricsPanel();
  } else {
    // Different track or not visible - show
    showLyricsPanel(trackIndex);
  }
}

/**
 * Update the excluded toggle button icon
 */
function updateExcludedToggleIcon(isVisible) {
  if (!elements.toggleExcludedBtn) return;

  // Get current badge state before replacing content
  const badgeText = elements.excludedBadge?.textContent || '0';
  const badgeDisplay = elements.excludedBadge?.style.display || 'none';

  const iconName = isVisible ? 'eye' : 'eye-off';
  elements.toggleExcludedBtn.innerHTML = `<i data-lucide="${iconName}"></i><span class="excluded-badge" id="excludedBadge" style="display: ${badgeDisplay};">${badgeText}</span>`;
  elements.toggleExcludedBtn.title = isVisible ? 'Hide excluded' : 'Show excluded';

  // Update the cached badge reference since we recreated it
  elements.excludedBadge = document.getElementById('excludedBadge');

  if (window.lucide) {
    window.lucide.createIcons({ nodes: [elements.toggleExcludedBtn] });
  }
}

/**
 * Update overlay UI from playlist state
 */
export function updateOverlayUI() {
  // Always update header stats regardless of overlay state —
  // the header (count, duration, excluded badge) is visible even when collapsed
  const includedTracks = playlistState.playlist.filter(t => t.included);
  const excludedCount = playlistState.playlist.length - includedTracks.length;
  updateExcludedBadge(excludedCount);

  if (elements.playlistOverlayCount) {
    elements.playlistOverlayCount.textContent = `${includedTracks.length} track${includedTracks.length !== 1 ? 's' : ''}`;
  }

  updateOverlayDurationDisplay();

  if (!playlistOverlayState.isOpen) return;

  // Render main playlist (included tracks only)
  renderPlaylistItems(includedTracks, false);

  // Render excluded list if visible
  if (playlistOverlayState.excludedVisible) {
    updateExcludedList();
  }

  // Reapply filter if active
  if (playlistOverlayState.filterText) {
    applyFilter();
  }

  // Update bulk exclude button state
  updateBulkExcludeButtonState();

  // Update current song display
  updateCurrentSongDisplay();
}

/**
 * Update the overlay playlist total duration display.
 * Hidden until all durations are loaded to avoid confusing progressive updates.
 */
function updateOverlayDurationDisplay() {
  if (!elements.playlistOverlayDuration) return;
  if (!playlistState.durationsLoaded) {
    elements.playlistOverlayDuration.textContent = '';
    return;
  }
  const totalSeconds = playlistState.playlist
    .filter(t => t.included && t.duration)
    .reduce((sum, t) => sum + t.duration, 0);
  elements.playlistOverlayDuration.textContent = formatPlaylistDuration(totalSeconds);
}

/**
 * Update the excluded badge count
 */
function updateExcludedBadge(count) {
  if (!elements.excludedBadge) return;

  if (count > 0) {
    elements.excludedBadge.textContent = count.toString();
    elements.excludedBadge.style.display = 'flex';
  } else {
    elements.excludedBadge.style.display = 'none';
  }
}

/**
 * Render playlist items
 */
function renderPlaylistItems(tracks, isExcluded) {
  const listEl = isExcluded ? elements.excludedList : elements.playlistOverlayList;
  if (!listEl) return;

  if (tracks.length === 0) {
    listEl.innerHTML = `
      <li class="playlist-overlay-empty">
        ${isExcluded ? 'No excluded tracks' : 'No tracks in playlist'}
      </li>
    `;
    return;
  }

  listEl.innerHTML = tracks.map((track) => {
    // Find original index in full playlist
    const originalIndex = playlistState.playlist.indexOf(track);
    const isPlaying = originalIndex === playlistState.currentTrackIndex;
    const isSelected = playlistState.selectedIndices.includes(originalIndex);
    const hasError = track.error;

    const classes = [
      'playlist-overlay-item',
      isPlaying ? 'playing' : '',
      isSelected ? 'selected' : '',
      hasError ? 'has-error' : ''
    ].filter(Boolean).join(' ');

    // For excluded items, show restore button with dynamic title based on selection
    const selectedExcludedCount = playlistState.selectedIndices.filter(idx =>
      idx >= 0 && idx < playlistState.playlist.length && !playlistState.playlist[idx].included
    ).length;
    const restoreTitle = isExcluded && selectedExcludedCount > 1 && isSelected
      ? `Restore ${selectedExcludedCount} selected tracks to playlist`
      : 'Restore to playlist';

    const actionBtn = isExcluded
      ? `<button class="playlist-item-action-btn restore-btn" data-index="${originalIndex}" title="${restoreTitle}">
           <i data-lucide="plus-circle"></i>
         </button>`
      : `<button class="playlist-item-action-btn duplicate-btn" data-index="${originalIndex}" title="Duplicate track">
           <i data-lucide="copy"></i>
         </button>
         <button class="playlist-item-action-btn exclude-btn" data-index="${originalIndex}" title="Exclude from playlist">
           <i data-lucide="x-circle"></i>
         </button>`;

    const duration = hasError ? 'Error' : (track.duration ? formatTime(track.duration) : '--:--');

    // Error indicator
    const errorIndicator = hasError
      ? `<span class="playlist-item-error-indicator" title="${track.errorMessage || 'Error loading file'}">
           <i data-lucide="alert-circle"></i>
         </span>`
      : '';

    // Lyrics button for included tracks only (hide if error)
    const lyricsClass = track.lyrics ? 'has-lyrics' : 'no-lyrics';
    const lyricsTitle = track.lyrics ? 'View/edit lyrics' : 'Add lyrics';
    const lyricsBtn = !isExcluded && !hasError
      ? `<button class="playlist-item-lyrics-btn ${lyricsClass}" data-index="${originalIndex}" title="${lyricsTitle}">
           <i data-lucide="file-text"></i>
         </button>`
      : '';

    return `
      <li class="${classes}" data-index="${originalIndex}">
        <div class="playlist-item-drag-handle" title="Drag to reorder">
          <i data-lucide="grip-vertical"></i>
        </div>
        ${errorIndicator}
        <div class="playlist-item-info">
          <span class="song-name">${escapeHtml(track.name)}</span>
          <span class="song-duration">${duration}</span>
        </div>
        ${lyricsBtn}
        ${actionBtn}
      </li>
    `;
  }).join('');

  // Attach event listeners
  attachItemListeners(listEl, isExcluded);

  // Recreate icons
  if (window.lucide) {
    window.lucide.createIcons({ nodes: [listEl] });
  }
}

/**
 * Update excluded list
 */
function updateExcludedList() {
  const excludedTracks = playlistState.playlist.filter(t => !t.included);

  // Update count
  if (elements.excludedCount) {
    elements.excludedCount.textContent = excludedTracks.length.toString();
  }

  renderPlaylistItems(excludedTracks, true);
}

/**
 * Attach event listeners to playlist items
 */
function attachItemListeners(listEl, isExcluded) {
  const items = listEl.querySelectorAll('.playlist-overlay-item');

  items.forEach(item => {
    const index = parseInt(item.dataset.index);

    // Click for selection
    item.addEventListener('click', (e) => {
      if (e.target.closest('.playlist-item-drag-handle')) return;
      if (e.target.closest('.playlist-item-action-btn')) return;
      if (e.target.closest('.playlist-item-lyrics-btn')) return;
      handleItemClick(index, e, isExcluded);
    });

    // Double-click to play (included tracks) or restore (excluded tracks)
    if (!isExcluded) {
      item.addEventListener('dblclick', (e) => {
        if (e.target.closest('.playlist-item-drag-handle')) return;
        if (e.target.closest('.playlist-item-action-btn')) return;
        if (e.target.closest('.playlist-item-lyrics-btn')) return;
        handleItemDoubleClick(index);
      });
    } else {
      // Double-click on excluded items restores them (or all selected)
      item.addEventListener('dblclick', (e) => {
        if (e.target.closest('.playlist-item-drag-handle')) return;
        if (e.target.closest('.playlist-item-action-btn')) return;
        restoreTrack(index);
      });
    }

    // Lyrics button
    const lyricsBtn = item.querySelector('.playlist-item-lyrics-btn');
    if (lyricsBtn) {
      lyricsBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        handleLyricsClick(index);
      });
    }

    // Duplicate button (included tracks only)
    const duplicateBtn = item.querySelector('.duplicate-btn');
    if (duplicateBtn) {
      duplicateBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        duplicateTrack(index);
      });
    }

    // Action button (exclude/restore)
    const excludeBtn = item.querySelector('.exclude-btn');
    if (excludeBtn) {
      excludeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        excludeTrack(index);
      });
    }

    const restoreBtn = item.querySelector('.restore-btn');
    if (restoreBtn) {
      restoreBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        restoreTrack(index);
      });
    }

    // Drag handle - enable for both included and excluded items
    const dragHandle = item.querySelector('.playlist-item-drag-handle');
    if (dragHandle) {
      setupDragHandlers(item, dragHandle, isExcluded);
    }
  });
}

/**
 * Handle item click (selection)
 * Works for both included and excluded lists
 * @param {number} index - The playlist index of the clicked item
 * @param {MouseEvent} event - The click event
 * @param {boolean} isExcludedList - Whether this is an excluded item
 */
function handleItemClick(index, event, isExcludedList = false) {
  const track = playlistState.playlist[index];
  if (!track) return;

  // Determine if we're selecting in the same list type as before
  const wasSelectingExcluded = playlistState.selectedIndices.length > 0 &&
    playlistState.selectedIndices.every(idx => !playlistState.playlist[idx]?.included);
  const isSelectingExcluded = !track.included;

  // If switching between list types, clear selection first
  if (playlistState.selectedIndices.length > 0 && wasSelectingExcluded !== isSelectingExcluded) {
    playlistState.selectedIndices = [];
    playlistState.lastSelectedIndex = -1;
  }

  if (event.shiftKey && playlistState.lastSelectedIndex >= 0) {
    // Range select - only within same list type
    const start = Math.min(playlistState.lastSelectedIndex, index);
    const end = Math.max(playlistState.lastSelectedIndex, index);
    playlistState.selectedIndices = [];
    for (let i = start; i <= end; i++) {
      const t = playlistState.playlist[i];
      // Only include tracks that match the current list type
      if (t && (isExcludedList ? !t.included : t.included)) {
        playlistState.selectedIndices.push(i);
      }
    }
  } else if (event.ctrlKey || event.metaKey) {
    // Toggle single selection
    const idx = playlistState.selectedIndices.indexOf(index);
    if (idx >= 0) {
      playlistState.selectedIndices.splice(idx, 1);
    } else {
      playlistState.selectedIndices.push(index);
    }
    playlistState.lastSelectedIndex = index;
  } else {
    // Single select
    playlistState.selectedIndices = [index];
    playlistState.lastSelectedIndex = index;
  }

  updateOverlayUI();
  // Sync with sidebar playlist
  document.dispatchEvent(new CustomEvent('playlistSelectionChanged'));
}

/**
 * Handle item double-click (play)
 */
async function handleItemDoubleClick(index) {
  // Block during export
  if (isPlaybackBlocked()) return;

  // Exit live mode if active
  if (uiState.isLiveMode) {
    const { exitLiveMode } = await import('./live-mode.js');
    exitLiveMode();
  }

  const loaded = await loadTrack(index);
  if (loaded) {
    startPlayback(0);
  }
}

/**
 * Exclude a track (move to excluded list)
 */
function excludeTrack(index) {
  // Block playlist modifications during export
  if (isPlaybackBlocked()) return;

  if (index >= 0 && index < playlistState.playlist.length) {
    const track = playlistState.playlist[index];
    playlistState.playlist[index].included = false;

    // Log the change
    const includedCount = playlistState.playlist.filter(t => t.included).length;
    const excludedCount = playlistState.playlist.filter(t => !t.included).length;
    const currentTrack = playlistState.playlist[playlistState.currentTrackIndex];
    const currentIsIncluded = currentTrack?.included ?? false;
    console.log(
      `[Playlist] Excluded: "${track.name}" | ` +
      `Lists: ${includedCount} included, ${excludedCount} excluded | ` +
      `Current track: "${currentTrack?.name || 'none'}" (index ${playlistState.currentTrackIndex}, ${currentIsIncluded ? 'included' : 'EXCLUDED'})`
    );

    // Update selection
    const selIdx = playlistState.selectedIndices.indexOf(index);
    if (selIdx >= 0) {
      playlistState.selectedIndices.splice(selIdx, 1);
    }

    updateOverlayUI();
    document.dispatchEvent(new CustomEvent('playlistUpdated'));
  }
}

/**
 * Restore a track (move back to main playlist)
 * If the clicked track is part of a multi-selection, restore all selected tracks
 */
function restoreTrack(index) {
  // Block playlist modifications during export
  if (isPlaybackBlocked()) return;

  // Check if the clicked track is part of a selection of excluded tracks
  const selectedExcluded = playlistState.selectedIndices.filter(idx =>
    idx >= 0 && idx < playlistState.playlist.length && !playlistState.playlist[idx].included
  );

  // If clicking on a selected excluded track and there are multiple selected, restore all
  if (selectedExcluded.includes(index) && selectedExcluded.length > 1) {
    restoreMultipleTracks(selectedExcluded);
    return;
  }

  // Single track restore
  if (index >= 0 && index < playlistState.playlist.length) {
    const track = playlistState.playlist[index];
    playlistState.playlist[index].included = true;

    // Log the change
    const includedCount = playlistState.playlist.filter(t => t.included).length;
    const excludedCount = playlistState.playlist.filter(t => !t.included).length;
    const currentTrack = playlistState.playlist[playlistState.currentTrackIndex];
    const currentIsIncluded = currentTrack?.included ?? false;
    console.log(
      `[Playlist] Restored: "${track.name}" | ` +
      `Lists: ${includedCount} included, ${excludedCount} excluded | ` +
      `Current track: "${currentTrack?.name || 'none'}" (index ${playlistState.currentTrackIndex}, ${currentIsIncluded ? 'included' : 'excluded'})`
    );

    // Clear selection after restore
    playlistState.selectedIndices = [];

    updateOverlayUI();
    document.dispatchEvent(new CustomEvent('playlistUpdated'));
  }
}

/**
 * Restore multiple tracks from excluded list
 * @param {number[]} indices - Array of track indices to restore
 */
function restoreMultipleTracks(indices) {
  // Block playlist modifications during export
  if (isPlaybackBlocked()) return;

  if (!indices || indices.length === 0) return;

  const restoredNames = [];
  for (const index of indices) {
    if (index >= 0 && index < playlistState.playlist.length) {
      const track = playlistState.playlist[index];
      if (!track.included) {
        track.included = true;
        restoredNames.push(track.name);
      }
    }
  }

  if (restoredNames.length > 0) {
    // Log the change
    const includedCount = playlistState.playlist.filter(t => t.included).length;
    const excludedCount = playlistState.playlist.filter(t => !t.included).length;
    console.log(
      `[Playlist] Restored ${restoredNames.length} track(s): ${restoredNames.join(', ')} | ` +
      `Lists: ${includedCount} included, ${excludedCount} excluded`
    );

    // Clear selection after restore
    playlistState.selectedIndices = [];

    updateOverlayUI();
    document.dispatchEvent(new CustomEvent('playlistUpdated'));
  }
}

/**
 * Duplicate a track (add a copy referencing the same audio data).
 * Memory-efficient: reuses the same file/audioBuffer/streamUrl references.
 */
function duplicateTrack(index) {
  // Block playlist modifications during export
  if (isPlaybackBlocked()) return;

  if (index >= 0 && index < playlistState.playlist.length) {
    const original = playlistState.playlist[index];

    // Create a shallow copy that references the same underlying audio data
    // This is memory-efficient: no duplication of file/audioBuffer/streamUrl
    const duplicate = {
      ...original,
      addedAt: Date.now() // New timestamp for the duplicate
    };

    // Insert after the original track
    const insertPosition = index + 1;
    playlistState.playlist.splice(insertPosition, 0, duplicate);

    // Update current track index if the duplicate was inserted before it
    if (playlistState.currentTrackIndex >= insertPosition) {
      playlistState.currentTrackIndex += 1;
    }

    console.log(`[Playlist] Duplicated: "${original.name}" at position ${insertPosition}`);

    updateOverlayUI();
    document.dispatchEvent(new CustomEvent('playlistUpdated'));
  }
}

/**
 * Setup drag handlers for an item
 * @param {HTMLElement} item - The playlist item element
 * @param {HTMLElement} dragHandle - The drag handle element
 * @param {boolean} isExcluded - Whether this is an excluded item
 */
function setupDragHandlers(item, dragHandle, isExcluded = false) {
  let dragPreview = null;

  const onDragStart = (e) => {
    e.preventDefault();

    // Block drag operations during export
    if (isPlaybackBlocked()) return;

    const index = parseInt(item.dataset.index);

    // Ensure this item is in selection, or make it the only selection
    if (!playlistState.selectedIndices.includes(index)) {
      playlistState.selectedIndices = [index];
      playlistState.lastSelectedIndex = index;
      updateOverlayUI();
    }

    // Initialize drag state
    playlistOverlayState.dragState = {
      isDragging: true,
      draggedIndices: [...playlistState.selectedIndices],
      dragStartY: e.clientY,
      currentY: e.clientY,
      dropTargetIndex: -1,
      fromExcluded: isExcluded
    };

    // Create drag preview with appropriate text
    const previewText = isExcluded ? 'Drop to restore' : 'Drop to reorder';
    dragPreview = createDragPreview(playlistState.selectedIndices.length, previewText);
    document.body.appendChild(dragPreview);
    updateDragPreviewPosition(dragPreview, e.clientX, e.clientY);

    // Mark dragged items
    playlistState.selectedIndices.forEach(idx => {
      const el = document.querySelector(`.playlist-overlay-item[data-index="${idx}"]`);
      if (el) el.classList.add('dragging');
    });

    // Add document listeners
    document.addEventListener('mousemove', onDragMove);
    document.addEventListener('mouseup', onDragEnd);
    // Clean up drag if window loses focus (e.g., Alt+Tab, tab switch)
    window.addEventListener('blur', onDragEnd);
    document.addEventListener('visibilitychange', onDragEnd);
  };

  const onDragMove = (e) => {
    if (!playlistOverlayState.dragState.isDragging) return;

    playlistOverlayState.dragState.currentY = e.clientY;

    // Update preview position
    if (dragPreview) {
      updateDragPreviewPosition(dragPreview, e.clientX, e.clientY);
    }

    // Find drop target - check both lists for cross-list dragging
    updateDropTarget(e.clientY, e.clientX);
  };

  const onDragEnd = async () => {
    if (!playlistOverlayState.dragState.isDragging) return;

    // Cleanup event listeners immediately
    document.removeEventListener('mousemove', onDragMove);
    document.removeEventListener('mouseup', onDragEnd);
    window.removeEventListener('blur', onDragEnd);
    document.removeEventListener('visibilitychange', onDragEnd);

    // Remove dragging class
    document.querySelectorAll('.playlist-overlay-item.dragging').forEach(el => {
      el.classList.remove('dragging');
    });

    // Remove drop indicators
    document.querySelectorAll('.drag-over-top, .drag-over-bottom, .drag-over-list').forEach(el => {
      el.classList.remove('drag-over-top', 'drag-over-bottom', 'drag-over-list');
    });

    // Remove preview
    if (dragPreview) {
      dragPreview.remove();
      dragPreview = null;
    }

    // Capture drag state before resetting (for async operations)
    const { dropTargetIndex, fromExcluded, draggedIndices } = playlistOverlayState.dragState;

    // Perform the appropriate operation
    if (dropTargetIndex >= 0) {
      if (fromExcluded) {
        // Dragging from excluded to main list - restore tracks
        restoreMultipleTracks(draggedIndices);
      } else {
        // Reorder within main list
        await performReorder();
      }
    }

    // Reset drag state
    playlistOverlayState.dragState = {
      isDragging: false,
      draggedIndices: [],
      dragStartY: 0,
      currentY: 0,
      dropTargetIndex: -1,
      fromExcluded: false
    };

    updateOverlayUI();
    document.dispatchEvent(new CustomEvent('playlistUpdated'));
  };

  dragHandle.addEventListener('mousedown', onDragStart);
}

/**
 * Create drag preview element
 * @param {number} count - Number of tracks being dragged
 * @param {string} text - Text to display (e.g., "Drop to reorder" or "Drop to restore")
 */
function createDragPreview(count, text = 'Drop to reorder') {
  const preview = document.createElement('div');
  preview.className = 'drag-preview';
  preview.innerHTML = `
    <div class="drag-preview-count">${count} track${count > 1 ? 's' : ''}</div>
    <div class="drag-preview-text">${text}</div>
  `;
  return preview;
}

/**
 * Update drag preview position
 */
function updateDragPreviewPosition(preview, x, y) {
  preview.style.left = `${x + 12}px`;
  preview.style.top = `${y + 12}px`;
}

/**
 * Update drop target indicator
 * @param {number} y - Mouse Y position
 * @param {number} x - Mouse X position (for cross-list detection)
 */
function updateDropTarget(y, x) {
  const mainListEl = elements.playlistOverlayList;
  const fromExcluded = playlistOverlayState.dragState.fromExcluded;

  // Clear existing indicators from both lists
  document.querySelectorAll('.drag-over-top, .drag-over-bottom, .drag-over-list').forEach(el => {
    el.classList.remove('drag-over-top', 'drag-over-bottom', 'drag-over-list');
  });

  // Reset drop target
  playlistOverlayState.dragState.dropTargetIndex = -1;

  // If dragging from excluded, check if cursor is over main list
  if (fromExcluded && mainListEl) {
    const mainListRect = mainListEl.getBoundingClientRect();
    if (x >= mainListRect.left && x <= mainListRect.right &&
        y >= mainListRect.top && y <= mainListRect.bottom) {
      // Over main list - highlight it and set drop target
      mainListEl.classList.add('drag-over-list');
      // Set drop target to end of included tracks
      const includedCount = playlistState.playlist.filter(t => t.included).length;
      playlistOverlayState.dragState.dropTargetIndex = includedCount;
      return;
    }
    // Not over main list - no valid drop target for excluded items
    return;
  }

  // Normal reorder within main list
  if (!mainListEl) return;

  // Find item under cursor (exclude dragged items)
  const items = Array.from(mainListEl.querySelectorAll('.playlist-overlay-item:not(.dragging)'));

  for (const item of items) {
    const rect = item.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;

    if (y >= rect.top && y <= rect.bottom) {
      if (y < midY) {
        item.classList.add('drag-over-top');
        playlistOverlayState.dragState.dropTargetIndex = parseInt(item.dataset.index);
      } else {
        item.classList.add('drag-over-bottom');
        playlistOverlayState.dragState.dropTargetIndex = parseInt(item.dataset.index) + 1;
      }
      return;
    }
  }

  // If below all items, target end
  if (items.length > 0) {
    const lastItem = items[items.length - 1];
    const rect = lastItem.getBoundingClientRect();
    if (y > rect.bottom) {
      lastItem.classList.add('drag-over-bottom');
      playlistOverlayState.dragState.dropTargetIndex = parseInt(lastItem.dataset.index) + 1;
    }
  }
}

/**
 * Perform the reorder operation
 * @param {boolean} skipPrompt - If true, skip the confirmation prompt (used when user has already confirmed)
 */
async function performReorder(skipPrompt = false) {
  const { draggedIndices, dropTargetIndex } = playlistOverlayState.dragState;

  if (dropTargetIndex < 0 || draggedIndices.length === 0) return;

  // If we're in a non-custom sort mode, show warning that this will overwrite custom order
  if (sortingState.mode !== 'custom' && !skipPrompt) {
    const confirmed = await sortingModal.show();

    if (!confirmed) {
      return; // User cancelled - don't reorder
    }
  }

  // Get reference to currently playing track before reordering
  const currentTrack = playlistState.playlist[playlistState.currentTrackIndex];

  // Sort dragged indices in descending order for removal
  const sortedIndices = [...draggedIndices].sort((a, b) => b - a);

  // Extract dragged items (in original order)
  const draggedItems = [...draggedIndices]
    .sort((a, b) => a - b)
    .map(idx => playlistState.playlist[idx]);

  // Remove items from their original positions (in descending order to maintain indices)
  for (const idx of sortedIndices) {
    playlistState.playlist.splice(idx, 1);
  }

  // Calculate new insert position (accounting for removed items)
  let insertPos = dropTargetIndex;
  for (const idx of sortedIndices) {
    if (idx < dropTargetIndex) {
      insertPos--;
    }
  }

  // Ensure insert position is valid
  insertPos = Math.max(0, Math.min(insertPos, playlistState.playlist.length));

  // Insert items at new position
  for (let i = 0; i < draggedItems.length; i++) {
    playlistState.playlist.splice(insertPos + i, 0, draggedItems[i]);
  }

  // Update current track index to follow the moved track
  if (currentTrack) {
    playlistState.currentTrackIndex = playlistState.playlist.indexOf(currentTrack);
  }

  // If we were in a non-custom mode and user confirmed, this becomes the new custom order
  if (sortingState.mode !== 'custom') {
    saveCurrentOrderAsCustom();
    sortingState.mode = 'custom';
    updateSortToggleUI();
  } else {
    // In custom mode - update the custom order reference
    saveCurrentOrderAsCustom();
  }

  // Update selection to new positions
  playlistState.selectedIndices = draggedItems.map(item =>
    playlistState.playlist.indexOf(item)
  );
}

/**
 * Setup keyboard shortcuts
 */
function setupKeyboardShortcuts() {
  document.addEventListener('keydown', (e) => {
    // ESC to close overlay (but not if cleanup modal is open or sorting modal is open)
    if (e.key === 'Escape' && playlistOverlayState.isOpen && !cleanupModal.isOpen && !sortingModal.isOpen) {
      closeOverlay();
      e.preventDefault();
    }
  });
}

// =====================================================
// Playlist Sorting
// =====================================================

/**
 * Setup sorting toggle buttons
 */
function setupSortingToggles() {
  const sortButtons = [
    elements.sortCustomBtn,
    elements.sortAlphaBtn,
    elements.sortDateBtn,
    elements.sortDurationBtn
  ];

  sortButtons.forEach(btn => {
    if (!btn) return;

    btn.addEventListener('click', () => {
      const sortMode = btn.dataset.sort;
      handleSortToggleClick(sortMode, btn);
    });
  });
}

/**
 * Setup filter input functionality
 */
function setupFilterInput() {
  if (!elements.playlistFilterInput) return;

  elements.playlistFilterInput.addEventListener('input', (e) => {
    playlistOverlayState.filterText = e.target.value.trim().toLowerCase();
    elements.playlistFilterContainer?.classList.toggle('has-value',
      playlistOverlayState.filterText.length > 0);
    applyFilter();
  });

  elements.playlistFilterClear?.addEventListener('click', clearFilter);

  elements.playlistFilterInput.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && playlistOverlayState.filterText) {
      clearFilter();
      e.stopPropagation();
    }
  });
}

/**
 * Clear the filter
 */
function clearFilter() {
  playlistOverlayState.filterText = '';
  if (elements.playlistFilterInput) {
    elements.playlistFilterInput.value = '';
  }
  elements.playlistFilterContainer?.classList.remove('has-value');
  applyFilter();
}

/**
 * Apply filter to playlist items (both main and excluded lists)
 * Combines text filter with Suno metadata filters
 */
function applyFilter() {
  const filterText = playlistOverlayState.filterText;
  const sunoFilters = playlistOverlayState.sunoFilters;
  const hasSunoFilters = playlistOverlayState.sunoFiltersActive;

  [elements.playlistOverlayList, elements.excludedList].forEach(list => {
    list?.querySelectorAll('.playlist-overlay-item').forEach(item => {
      const index = parseInt(item.dataset.index);
      const track = playlistState.playlist[index];
      if (!track) return;

      // Check text filter (name)
      const name = item.querySelector('.song-name')?.textContent.toLowerCase() || '';
      const matchesText = !filterText || name.includes(filterText);

      // Check Suno metadata filters
      const matchesSuno = !hasSunoFilters || checkSunoFilters(track, sunoFilters);

      item.classList.toggle('filtered-out', !matchesText || !matchesSuno);
    });
  });
}

/**
 * Check if a track matches Suno metadata filters
 */
function checkSunoFilters(track, filters) {
  const meta = track.sunoMeta;

  // If track has no Suno metadata, only show if no Suno filters are active
  if (!meta) {
    return !filters.genre && !filters.theme && filters.audioType === 'all' &&
           filters.likedStatus === 'all' && !filters.dateFrom && !filters.dateTo;
  }

  // Genre/tags filter
  if (filters.genre) {
    const genreSearch = filters.genre.toLowerCase();
    const tags = (meta.tags || '').toLowerCase();
    const prompt = (meta.prompt || '').toLowerCase();
    if (!tags.includes(genreSearch) && !prompt.includes(genreSearch)) {
      return false;
    }
  }

  // Theme/prompt filter
  if (filters.theme) {
    const themeSearch = filters.theme.toLowerCase();
    const prompt = (meta.prompt || '').toLowerCase();
    const name = (track.name || '').toLowerCase();
    if (!prompt.includes(themeSearch) && !name.includes(themeSearch)) {
      return false;
    }
  }

  // Audio type filter
  if (filters.audioType !== 'all') {
    const isInstrumental = meta.isInstrumental;
    if (filters.audioType === 'instrumental' && !isInstrumental) return false;
    if (filters.audioType === 'vocal' && isInstrumental) return false;
  }

  // Liked status filter
  if (filters.likedStatus !== 'all') {
    const isLiked = meta.isLiked;
    if (filters.likedStatus === 'liked' && !isLiked) return false;
    if (filters.likedStatus === 'not-liked' && isLiked) return false;
  }

  // Date range filter
  if (filters.dateFrom || filters.dateTo) {
    const createdAt = parseDateSafe(meta.createdAt);
    if (!createdAt) return false; // No date = doesn't match date filter

    if (filters.dateFrom) {
      const fromDate = parseDateSafe(filters.dateFrom);
      if (fromDate && createdAt < fromDate) return false;
    }
    if (filters.dateTo) {
      const toDate = parseDateSafe(filters.dateTo);
      if (toDate) {
        // Include the entire end date (set to end of day)
        const endOfDay = new Date(toDate);
        endOfDay.setHours(23, 59, 59, 999);
        if (createdAt > endOfDay) return false;
      }
    }
  }

  return true;
}

/**
 * Parse a date string safely, handling various formats
 * @param {string|Date|null|undefined} dateStr - Date to parse
 * @returns {Date|null} Parsed date or null if invalid
 */
function parseDateSafe(dateStr) {
  if (!dateStr) return null;

  // Already a Date object
  if (dateStr instanceof Date) {
    return isNaN(dateStr.getTime()) ? null : dateStr;
  }

  // Try parsing the string
  try {
    const parsed = new Date(dateStr);
    return isNaN(parsed.getTime()) ? null : parsed;
  } catch {
    return null;
  }
}

/**
 * Setup bulk exclude button
 */
function setupBulkExcludeButton() {
  if (!elements.bulkExcludeBtn) return;

  elements.bulkExcludeBtn.addEventListener('click', bulkExclude);
  updateBulkExcludeButtonState();
}

/**
 * Update bulk exclude button state and tooltip
 * This button is multi-purpose:
 * - For included tracks: excludes them (moves to excluded list)
 * - For excluded tracks: permanently deletes them from the playlist
 */
function updateBulkExcludeButtonState() {
  if (!elements.bulkExcludeBtn) return;

  const hasPlaylist = playlistState.playlist.length > 0;
  const hasIncluded = playlistState.playlist.some(t => t.included);
  const hasExcluded = playlistState.playlist.some(t => !t.included);
  const selCount = playlistState.selectedIndices.length;
  const currentIncluded = playlistState.playlist[playlistState.currentTrackIndex]?.included;

  // Check if selection contains excluded tracks
  const selectedExcludedCount = playlistState.selectedIndices.filter(idx =>
    idx >= 0 && idx < playlistState.playlist.length && !playlistState.playlist[idx].included
  ).length;
  const selectedIncludedCount = selCount - selectedExcludedCount;
  const allSelectedAreExcluded = selCount > 0 && selectedExcludedCount === selCount;

  if (!hasPlaylist) {
    elements.bulkExcludeBtn.disabled = true;
    elements.bulkExcludeBtn.title = 'No tracks in playlist';
  } else if (allSelectedAreExcluded) {
    // All selected tracks are excluded - show delete action
    elements.bulkExcludeBtn.disabled = false;
    elements.bulkExcludeBtn.title = `Permanently remove ${selectedExcludedCount} selected track${selectedExcludedCount > 1 ? 's' : ''}`;
  } else if (selCount > 0 && selectedIncludedCount > 0) {
    // Some or all selected tracks are included - show exclude action
    elements.bulkExcludeBtn.disabled = false;
    elements.bulkExcludeBtn.title = `Exclude ${selectedIncludedCount} selected track${selectedIncludedCount > 1 ? 's' : ''}`;
  } else if (hasIncluded && currentIncluded) {
    elements.bulkExcludeBtn.disabled = false;
    elements.bulkExcludeBtn.title = 'Exclude current track';
  } else if (!hasIncluded && hasExcluded) {
    // Only excluded tracks exist, no selection - button disabled
    elements.bulkExcludeBtn.disabled = true;
    elements.bulkExcludeBtn.title = 'Select excluded tracks to delete';
  } else {
    elements.bulkExcludeBtn.disabled = true;
    elements.bulkExcludeBtn.title = 'No track to exclude';
  }
}

/**
 * Perform bulk exclude or delete operation based on selected tracks
 * - If selected tracks are included: exclude them (move to excluded list)
 * - If selected tracks are excluded: permanently delete them
 */
function bulkExclude() {
  // Block playlist modifications during export
  if (isPlaybackBlocked()) return;

  const selCount = playlistState.selectedIndices.length;

  // Check which type of tracks are selected
  const selectedExcluded = playlistState.selectedIndices.filter(idx =>
    idx >= 0 && idx < playlistState.playlist.length && !playlistState.playlist[idx].included
  );
  const selectedIncluded = playlistState.selectedIndices.filter(idx =>
    idx >= 0 && idx < playlistState.playlist.length && playlistState.playlist[idx]?.included
  );

  // If all selected are excluded tracks, permanently delete them
  if (selCount > 0 && selectedExcluded.length === selCount) {
    deleteExcludedTracks(selectedExcluded);
    return;
  }

  // Otherwise, exclude the included tracks (original behavior)
  const toExclude = [];

  if (selCount > 0) {
    // Exclude selected included tracks only
    toExclude.push(...selectedIncluded);
  } else {
    // Exclude current track only
    const idx = playlistState.currentTrackIndex;
    if (playlistState.playlist[idx]?.included) {
      toExclude.push(idx);
    }
  }

  if (toExclude.length === 0) return;

  for (const idx of toExclude) {
    playlistState.playlist[idx].included = false;
  }

  playlistState.selectedIndices = [];
  updateOverlayUI();
  document.dispatchEvent(new CustomEvent('playlistUpdated'));
}

/**
 * Permanently delete excluded tracks from the playlist
 * @param {number[]} indices - Array of track indices to delete (must be excluded tracks)
 */
function deleteExcludedTracks(indices) {
  if (!indices || indices.length === 0) return;

  // Validate all indices are excluded tracks
  const validIndices = indices.filter(idx => {
    if (idx < 0 || idx >= playlistState.playlist.length) return false;
    return !playlistState.playlist[idx].included;
  });

  if (validIndices.length === 0) return;

  // Get current track reference before deletion
  const currentTrack = playlistState.playlist[playlistState.currentTrackIndex];
  const currentTrackIncluded = currentTrack?.included;

  // Get track names for logging and tracks to delete
  // Sort in descending order to remove from end first (maintains correct indices)
  const sortedIndices = [...validIndices].sort((a, b) => b - a);
  const deletedNames = [];
  const deletedTracks = [];

  for (const idx of sortedIndices) {
    const track = playlistState.playlist[idx];
    deletedNames.unshift(track.name); // unshift to maintain original order in log
    deletedTracks.unshift(track);
    playlistState.playlist.splice(idx, 1);
  }

  // Update current track index
  if (currentTrack && currentTrackIncluded) {
    // Current track is included - find its new index
    playlistState.currentTrackIndex = playlistState.playlist.indexOf(currentTrack);
    if (playlistState.currentTrackIndex < 0) {
      // Shouldn't happen if current track was included, but handle gracefully
      playlistState.currentTrackIndex = 0;
    }
  } else if (currentTrack) {
    // Current track was excluded - check if it was deleted
    const newIndex = playlistState.playlist.indexOf(currentTrack);
    if (newIndex >= 0) {
      playlistState.currentTrackIndex = newIndex;
    } else {
      // Current track was deleted - set to first included track or 0
      const firstIncludedIndex = playlistState.playlist.findIndex(t => t.included);
      playlistState.currentTrackIndex = firstIncludedIndex >= 0 ? firstIncludedIndex : 0;
    }
  } else if (playlistState.playlist.length === 0) {
    playlistState.currentTrackIndex = 0;
  }

  // Update custom order if in custom mode
  if (sortingState.mode === 'custom') {
    sortingState.customOrder = sortingState.customOrder.filter(t => !deletedTracks.includes(t));
  }

  // Log the deletion
  const includedCount = playlistState.playlist.filter(t => t.included).length;
  const excludedCount = playlistState.playlist.filter(t => !t.included).length;
  console.log(
    `[Playlist] Permanently deleted ${deletedNames.length} track(s): ${deletedNames.join(', ')} | ` +
    `Remaining: ${includedCount} included, ${excludedCount} excluded`
  );

  // Clear selection
  playlistState.selectedIndices = [];

  updateOverlayUI();
  document.dispatchEvent(new CustomEvent('playlistUpdated'));
}

/**
 * Update the current song display in the overlay header
 */
function updateCurrentSongDisplay() {
  if (!elements.playlistCurrentSong || !elements.currentSongName) return;

  const currentTrack = playlistState.playlist[playlistState.currentTrackIndex];
  const name = currentTrack?.name || '';
  const gap = '\u00A0\u00A0\u00A0\u2022\u00A0\u00A0\u00A0'; // nbsp • nbsp gap

  // Create inner wrapper - duplicate text for continuous scroll effect
  const safeName = escapeHtml(name);
  elements.currentSongName.innerHTML = `<span class="current-song-name-inner">${safeName}${gap}${safeName}${gap}</span>`;

  // Check for overflow after render
  requestAnimationFrame(() => {
    const container = elements.currentSongName;
    const inner = container.querySelector('.current-song-name-inner');
    if (!inner) return;

    // Measure single copy width (half of total since we duplicated)
    const totalWidth = inner.scrollWidth;
    const singleCopyWidth = totalWidth / 2;
    const containerWidth = container.clientWidth;

    if (singleCopyWidth > containerWidth + 5) {
      // Text overflows - enable continuous marquee
      elements.playlistCurrentSong.classList.add('marquee-active');
      // Duration based on single copy width (~40px per second)
      const duration = Math.max(6, singleCopyWidth / 40);
      container.style.setProperty('--marquee-duration', `${duration}s`);
    } else {
      // No overflow - show single copy without animation
      elements.currentSongName.innerHTML = `<span class="current-song-name-inner">${safeName}</span>`;
      elements.playlistCurrentSong.classList.remove('marquee-active');
    }
  });
}

/**
 * Setup resize observer for current song display
 * Hides the element when its width is squeezed to 0
 */
function setupCurrentSongResizeObserver() {
  if (!elements.playlistCurrentSong) return;

  const titleContainer = elements.playlistCurrentSong.closest('.playlist-overlay-title');
  if (!titleContainer) return;

  const checkFit = () => {
    // Temporarily show to measure
    elements.playlistCurrentSong.classList.remove('hidden-by-resize');

    // Double rAF to ensure layout is complete after removing hidden class
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const width = elements.playlistCurrentSong.getBoundingClientRect().width;

        // Hide only when width reaches essentially 0
        if (width < 5) {
          elements.playlistCurrentSong.classList.add('hidden-by-resize');
        }
      });
    });
  };

  // Observe the parent container so we still get events when element is hidden
  const resizeObserver = new ResizeObserver(checkFit);
  resizeObserver.observe(titleContainer);
}

/**
 * Handle sort toggle click
 */
function handleSortToggleClick(sortMode, btn) {
  const currentMode = sortingState.mode;

  if (sortMode === currentMode && sortMode !== 'custom') {
    // Same mode - toggle direction
    sortingState.direction = sortingState.direction === 'asc' ? 'desc' : 'asc';
    updateSortDirectionUI(btn);
    applySorting();
  } else if (sortMode !== currentMode) {
    // Different mode - switch to it
    if (sortMode === 'custom') {
      // Switching to custom - restore saved custom order
      restoreCustomOrder();
      sortingState.mode = 'custom';
      updateSortToggleUI();
    } else {
      // Switching from custom to a sort mode - save custom order first
      if (currentMode === 'custom') {
        saveCurrentOrderAsCustom();
      }
      // Apply the new sort mode (no prompt needed)
      sortingState.mode = sortMode;
      sortingState.direction = 'asc';
      updateSortToggleUI();
      applySorting();
    }
  }
}

/**
 * Update sorting toggle UI to reflect current state
 */
function updateSortToggleUI() {
  const sortButtons = [
    elements.sortCustomBtn,
    elements.sortAlphaBtn,
    elements.sortDateBtn,
    elements.sortDurationBtn
  ];

  sortButtons.forEach(btn => {
    if (!btn) return;

    const isActive = btn.dataset.sort === sortingState.mode;
    btn.classList.toggle('active', isActive);

    // Update direction class for non-custom buttons
    if (btn.dataset.sort !== 'custom') {
      btn.classList.toggle('desc', sortingState.direction === 'desc');
    }
  });

  // Recreate icons
  if (window.lucide && elements.playlistSortToggles) {
    window.lucide.createIcons({ nodes: [elements.playlistSortToggles] });
  }
}

/**
 * Update sort direction UI for a specific button
 */
function updateSortDirectionUI(btn) {
  if (!btn || btn.dataset.sort === 'custom') return;

  btn.classList.toggle('desc', sortingState.direction === 'desc');

  // Recreate icons
  if (window.lucide) {
    window.lucide.createIcons({ nodes: [btn] });
  }
}

/**
 * Apply current sorting to playlist
 */
function applySorting() {
  const includedTracks = playlistState.playlist.filter(t => t.included);

  if (includedTracks.length === 0) return;

  // Get current track reference
  const currentTrack = playlistState.playlist[playlistState.currentTrackIndex];

  // Sort included tracks
  const sortedTracks = [...includedTracks].sort((a, b) => {
    let comparison = 0;

    switch (sortingState.mode) {
      case 'alphanumeric':
        comparison = a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
        break;
      case 'date':
        // Use addedAt timestamp, fallback to 0 if not present
        const dateA = a.addedAt || 0;
        const dateB = b.addedAt || 0;
        comparison = dateA - dateB;
        break;
      case 'duration':
        // Use duration, fallback to Infinity for unknown
        const durA = a.duration !== null ? a.duration : Infinity;
        const durB = b.duration !== null ? b.duration : Infinity;
        comparison = durA - durB;
        break;
      default:
        return 0;
    }

    return sortingState.direction === 'desc' ? -comparison : comparison;
  });

  // Get excluded tracks (maintain their order)
  const excludedTracks = playlistState.playlist.filter(t => !t.included);

  // Rebuild playlist: sorted included + excluded
  playlistState.playlist = [...sortedTracks, ...excludedTracks];

  // Update current track index
  if (currentTrack) {
    playlistState.currentTrackIndex = playlistState.playlist.indexOf(currentTrack);
  }

  // Clear selection
  playlistState.selectedIndices = [];

  // Update UI
  updateOverlayUI();
  document.dispatchEvent(new CustomEvent('playlistUpdated'));
}

/**
 * Save current playlist order as custom order
 */
function saveCurrentOrderAsCustom() {
  // Store references to tracks in their current order
  sortingState.customOrder = playlistState.playlist.filter(t => t.included).map(t => t);
}

/**
 * Restore custom order
 */
function restoreCustomOrder() {
  if (sortingState.customOrder.length === 0) return;

  const currentTrack = playlistState.playlist[playlistState.currentTrackIndex];

  // Get excluded tracks
  const excludedTracks = playlistState.playlist.filter(t => !t.included);

  // Filter custom order to only include tracks still in playlist
  const validCustomOrder = sortingState.customOrder.filter(t =>
    playlistState.playlist.includes(t) && t.included
  );

  // Find any new tracks not in custom order
  const newTracks = playlistState.playlist.filter(t =>
    t.included && !sortingState.customOrder.includes(t)
  );

  // Rebuild playlist: valid custom order + new tracks + excluded
  playlistState.playlist = [...validCustomOrder, ...newTracks, ...excludedTracks];

  // Update current track index
  if (currentTrack) {
    playlistState.currentTrackIndex = playlistState.playlist.indexOf(currentTrack);
  }

  // Clear selection
  playlistState.selectedIndices = [];

  // Update UI
  updateOverlayUI();
  document.dispatchEvent(new CustomEvent('playlistUpdated'));
}

/**
 * Update custom order from current playlist state
 * Called when playlist is modified (tracks added/removed)
 */
function updateCustomOrderFromPlaylist() {
  // Only update if in custom mode
  if (sortingState.mode === 'custom') {
    saveCurrentOrderAsCustom();
  }
}

/**
 * Initialize custom order when playlist is first loaded
 * Should be called after files are added
 */
export function initializeCustomOrder() {
  if (sortingState.customOrder.length === 0) {
    saveCurrentOrderAsCustom();
  }
}

// =====================================================
// Suno Metadata Filters
// =====================================================

// Debounce timer for Suno filter inputs
let sunoFilterDebounceTimer = null;
const SUNO_FILTER_DEBOUNCE_MS = 300;

/**
 * Setup Suno metadata filter controls
 */
function setupSunoFilters() {
  // Toggle panel visibility
  if (elements.sunoFilterBtn) {
    elements.sunoFilterBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      toggleSunoFilterPanel();
    });
  }

  // Close panel when clicking outside
  document.addEventListener('click', (e) => {
    if (elements.sunoFilterPanel?.classList.contains('visible') &&
        !e.target.closest('.suno-filter-dropdown')) {
      closeSunoFilterPanel();
    }
  });

  // Clear all filters button
  if (elements.sunoFilterClearAll) {
    elements.sunoFilterClearAll.addEventListener('click', (e) => {
      e.stopPropagation();
      clearAllSunoFilters();
    });
  }

  // Genre input (with debounce)
  if (elements.sunoFilterGenre) {
    elements.sunoFilterGenre.addEventListener('input', () => {
      debouncedSunoFilterUpdate();
    });
  }

  // Theme input (with debounce)
  if (elements.sunoFilterTheme) {
    elements.sunoFilterTheme.addEventListener('input', () => {
      debouncedSunoFilterUpdate();
    });
  }

  // Audio type buttons
  document.querySelectorAll('.suno-filter-option[data-filter="audioType"]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const value = btn.dataset.value;
      playlistOverlayState.sunoFilters.audioType = value;
      updateSunoFilterButtonState('audioType', value);
      updateSunoFiltersActive();
      applyFilter();
    });
  });

  // Liked status buttons
  document.querySelectorAll('.suno-filter-option[data-filter="likedStatus"]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const value = btn.dataset.value;
      playlistOverlayState.sunoFilters.likedStatus = value;
      updateSunoFilterButtonState('likedStatus', value);
      updateSunoFiltersActive();
      applyFilter();
    });
  });

  // Date inputs
  if (elements.sunoFilterDateFrom) {
    elements.sunoFilterDateFrom.addEventListener('change', (e) => {
      playlistOverlayState.sunoFilters.dateFrom = e.target.value || null;
      updateSunoFiltersActive();
      applyFilter();
    });
  }

  if (elements.sunoFilterDateTo) {
    elements.sunoFilterDateTo.addEventListener('change', (e) => {
      playlistOverlayState.sunoFilters.dateTo = e.target.value || null;
      updateSunoFiltersActive();
      applyFilter();
    });
  }
}

/**
 * Debounced update for Suno filter text inputs
 */
function debouncedSunoFilterUpdate() {
  if (sunoFilterDebounceTimer) {
    clearTimeout(sunoFilterDebounceTimer);
  }

  sunoFilterDebounceTimer = setTimeout(() => {
    // Read current values
    playlistOverlayState.sunoFilters.genre = elements.sunoFilterGenre?.value?.trim().toLowerCase() || '';
    playlistOverlayState.sunoFilters.theme = elements.sunoFilterTheme?.value?.trim().toLowerCase() || '';
    updateSunoFiltersActive();
    applyFilter();
  }, SUNO_FILTER_DEBOUNCE_MS);
}

/**
 * Toggle Suno filter panel visibility
 */
function toggleSunoFilterPanel() {
  if (elements.sunoFilterPanel?.classList.contains('visible')) {
    closeSunoFilterPanel();
  } else {
    openSunoFilterPanel();
  }
}

/**
 * Open Suno filter panel
 */
function openSunoFilterPanel() {
  if (elements.sunoFilterPanel) {
    elements.sunoFilterPanel.classList.add('visible');
  }
  if (elements.sunoFilterBtn) {
    elements.sunoFilterBtn.classList.add('active');
  }
  // Recreate icons
  if (window.lucide && elements.sunoFilterPanel) {
    window.lucide.createIcons({ nodes: [elements.sunoFilterPanel] });
  }
}

/**
 * Close Suno filter panel
 */
function closeSunoFilterPanel() {
  if (elements.sunoFilterPanel) {
    elements.sunoFilterPanel.classList.remove('visible');
  }
  if (elements.sunoFilterBtn) {
    elements.sunoFilterBtn.classList.remove('active');
  }
}

/**
 * Clear all Suno filters
 */
function clearAllSunoFilters() {
  playlistOverlayState.sunoFilters = {
    genre: '',
    theme: '',
    audioType: 'all',
    likedStatus: 'all',
    dateFrom: null,
    dateTo: null
  };
  playlistOverlayState.sunoFiltersActive = false;

  // Reset UI
  if (elements.sunoFilterGenre) elements.sunoFilterGenre.value = '';
  if (elements.sunoFilterTheme) elements.sunoFilterTheme.value = '';
  if (elements.sunoFilterDateFrom) elements.sunoFilterDateFrom.value = '';
  if (elements.sunoFilterDateTo) elements.sunoFilterDateTo.value = '';

  updateSunoFilterButtonState('audioType', 'all');
  updateSunoFilterButtonState('likedStatus', 'all');
  updateSunoFilterBadge();

  applyFilter();
}

/**
 * Update button state for a filter group
 */
function updateSunoFilterButtonState(filterType, activeValue) {
  document.querySelectorAll(`.suno-filter-option[data-filter="${filterType}"]`).forEach(btn => {
    btn.classList.toggle('active', btn.dataset.value === activeValue);
  });
}

/**
 * Update whether any Suno filters are active
 */
function updateSunoFiltersActive() {
  const filters = playlistOverlayState.sunoFilters;
  playlistOverlayState.sunoFiltersActive =
    !!filters.genre ||
    !!filters.theme ||
    filters.audioType !== 'all' ||
    filters.likedStatus !== 'all' ||
    !!filters.dateFrom ||
    !!filters.dateTo;

  updateSunoFilterBadge();
}

/**
 * Update the filter badge to show active filter count
 */
function updateSunoFilterBadge() {
  if (!elements.sunoFilterBadge) return;

  const filters = playlistOverlayState.sunoFilters;
  let count = 0;
  if (filters.genre) count++;
  if (filters.theme) count++;
  if (filters.audioType !== 'all') count++;
  if (filters.likedStatus !== 'all') count++;
  if (filters.dateFrom || filters.dateTo) count++;

  if (count > 0) {
    elements.sunoFilterBadge.textContent = count.toString();
    elements.sunoFilterBadge.style.display = 'flex';
  } else {
    elements.sunoFilterBadge.style.display = 'none';
  }
}
